import React, { useState, useEffect } from 'react';

import { GeoLocation, GeoLocationZone } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { EditDepartmentGeoZoneIconButton, calculatePolygonCenter, wktToLatLng } from '@vlabs/monokit/features/point/edit-department-geo-zone';
import { useGetDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './DepartmentGeoZone.module.sass';

const DEFAULT_MAP_CENTER: [number, number] = [25.2298, 55.2893]; // Dubai

const displayName = 'DepartmentGeoZone';
const testid = buildTestid(displayName);

interface DepartmentsGeoZoneProps extends WithClassNameProp, WithDataTestid {
  departmentId: DepartmentId;
}

function DepartmentGeoZone({
  departmentId,
  className,
  'data-testid': dataTestId = undefined,
}: DepartmentsGeoZoneProps): React.JSX.Element {
  const [center, setCenter] = useState(DEFAULT_MAP_CENTER);
  const getDepartment = useGetDepartment(departmentId, {
    query: { enabled: !!departmentId },
  });

  const wktZone = getDepartment.data?.zone_coordinates;

  useEffect(() => {
    const points = wktToLatLng(wktZone);

    if (points.length > 0) {
      const mapCenter = calculatePolygonCenter(points);
      setCenter(mapCenter);
    }
  }, [wktZone]);

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <div className={st.EditAction}>
        <EditDepartmentGeoZoneIconButton departmentId={departmentId} />
      </div>

      <GeoLocation center={center}>
        <GeoLocationZone wkt={wktZone} />
      </GeoLocation>
    </div>
  );
}

DepartmentGeoZone.displayName = displayName;

export { DepartmentGeoZone };
